import type { CampaignListSortField, CampaignStatusName } from "@multi-chat/shared";

export const CAMPAIGN_LIST_DATA_COLUMNS = [
  "name",
  "audience",
  "status",
  "updated",
  "scheduled",
] as const;

export type CampaignListDataColumnId = (typeof CAMPAIGN_LIST_DATA_COLUMNS)[number];

export const CAMPAIGN_LIST_DEFAULT_COLUMN_ORDER: CampaignListDataColumnId[] = [
  "name",
  "audience",
  "status",
  "updated",
  "scheduled",
];

export const CAMPAIGN_LIST_REQUIRED_COLUMNS: ReadonlySet<CampaignListDataColumnId> = new Set([
  "name",
]);

export const CAMPAIGN_LIST_CHECKBOX_WIDTH = 40;
export const CAMPAIGN_LIST_ACTIONS_WIDTH = 44;

export const CAMPAIGN_LIST_DEFAULT_WIDTHS: Record<CampaignListDataColumnId, number> = {
  name: 280,
  audience: 160,
  status: 160,
  updated: 200,
  scheduled: 200,
};

export const CAMPAIGN_LIST_MIN_WIDTHS: Record<CampaignListDataColumnId, number> = {
  name: 140,
  audience: 110,
  status: 110,
  updated: 140,
  scheduled: 140,
};

export const CAMPAIGN_LIST_MAX_WIDTHS: Record<CampaignListDataColumnId, number> = {
  name: 420,
  audience: 320,
  status: 300,
  updated: 360,
  scheduled: 360,
};

export const CAMPAIGN_LIST_SORTABLE_COLUMNS: ReadonlyArray<{
  columnId: CampaignListDataColumnId;
  field: CampaignListSortField;
}> = [
  { columnId: "name", field: "name" },
  { columnId: "status", field: "status" },
  { columnId: "updated", field: "updatedAt" },
  { columnId: "scheduled", field: "scheduledAt" },
];

export function isCampaignListDataColumnId(value: string): value is CampaignListDataColumnId {
  return (CAMPAIGN_LIST_DATA_COLUMNS as readonly string[]).includes(value);
}

export function normalizeCampaignColumnOrder(
  order: readonly string[],
): CampaignListDataColumnId[] {
  const next: CampaignListDataColumnId[] = [];
  for (const id of order) {
    if (isCampaignListDataColumnId(id) && !next.includes(id)) next.push(id);
  }
  for (const id of CAMPAIGN_LIST_DEFAULT_COLUMN_ORDER) {
    if (!next.includes(id)) next.push(id);
  }
  return next;
}

export function clampCampaignColumnWidth(id: CampaignListDataColumnId, width: number): number {
  return Math.min(CAMPAIGN_LIST_MAX_WIDTHS[id], Math.max(CAMPAIGN_LIST_MIN_WIDTHS[id], Math.round(width)));
}

export function campaignColumnSortField(
  columnId: CampaignListDataColumnId,
): CampaignListSortField | null {
  return CAMPAIGN_LIST_SORTABLE_COLUMNS.find((entry) => entry.columnId === columnId)?.field ?? null;
}

export function campaignRowPrimaryAction(status: CampaignStatusName): "edit" | "open" | "results" {
  switch (status) {
    case "DRAFT":
      return "edit";
    case "COMPLETED":
    case "FAILED":
    case "CANCELLED":
      return "results";
    case "SCHEDULED":
    case "RUNNING":
      return "open";
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

export function campaignFixedColumnsWidth(canSelect: boolean): number {
  return (canSelect ? CAMPAIGN_LIST_CHECKBOX_WIDTH : 0) + CAMPAIGN_LIST_ACTIONS_WIDTH;
}

export function campaignVisibleColumnsWidth(
  visible: readonly CampaignListDataColumnId[],
  widths: Record<CampaignListDataColumnId, number>,
): number {
  let total = 0;
  for (const id of visible) total += widths[id];
  return total;
}

export function reorderCampaignColumns(
  order: readonly CampaignListDataColumnId[],
  fromId: CampaignListDataColumnId,
  toId: CampaignListDataColumnId,
): CampaignListDataColumnId[] {
  if (fromId === toId) return [...order];
  const from = order.indexOf(fromId);
  const to = order.indexOf(toId);
  if (from < 0 || to < 0) return [...order];
  const next = [...order];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item!);
  return next;
}

export function campaignBalancedFillWidth(input: {
  containerWidth: number;
  canSelect: boolean;
  visible: readonly CampaignListDataColumnId[];
  widths: Record<CampaignListDataColumnId, number>;
  userSized: ReadonlySet<CampaignListDataColumnId>;
}): Record<CampaignListDataColumnId, number> {
  const next = { ...input.widths };
  const autoIds = input.visible.filter((id) => !input.userSized.has(id));
  if (autoIds.length === 0 || input.containerWidth <= 0) return next;
  const userWidth = campaignVisibleColumnsWidth(
    input.visible.filter((id) => input.userSized.has(id)),
    next,
  );
  const available =
    input.containerWidth - campaignFixedColumnsWidth(input.canSelect) - userWidth;
  const weightSum = autoIds.reduce((sum, id) => sum + CAMPAIGN_LIST_DEFAULT_WIDTHS[id], 0);
  if (available <= 0 || weightSum <= 0) return next;
  for (const id of autoIds) {
    next[id] = clampCampaignColumnWidth(
      id,
      (CAMPAIGN_LIST_DEFAULT_WIDTHS[id] / weightSum) * available,
    );
  }
  return next;
}

let campaignRowNavSuppressedUntil = 0;

export function suppressCampaignRowNavigation(ms = 750): void {
  campaignRowNavSuppressedUntil = Date.now() + ms;
}

export function shouldSuppressCampaignRowNavigation(): boolean {
  return Date.now() < campaignRowNavSuppressedUntil;
}
